import {
  CardioOrResistance,
  ProcessTypes, UserProfile, WorkoutSettings,
} from 'fitbeat.gym-core';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { translate } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { connect } from 'react-redux';
import { Equipment } from 'fitbeat.models';
import { UserServiceClient } from 'fitbeat.api-clients';
import { changeOrientation, Orientation, workoutSettingsIsUpdatingSelector } from '../../HomeWorkouts';
import { ThemeType } from '../../theme';
import {
  clearPersonaliseSetupWarning,
  getNextBookingStep,
  openPersonaliseWorkoutSetupScreen,
  setDefaultEquipment,
  workoutSettingsUpdated,
} from '../actions';
import {
  personaliseSetupScreenSelector, personaliseSetupWarningSelector, recommendedSettingsSelector,
} from '../selectors';
import { PersonaliseWorkoutFooter } from './personaliseWorkoutSteps/PersonaliseWorkoutFooter';
import {
  PersonaliseSetupWarning, PersonaliseWorkoutSetup,
} from './personaliseWorkoutSteps/PersonaliseWorkoutSetup';
import { PersonaliseSetupScreens } from './personaliseWorkoutSteps/types';
import { getApiConfig } from '../../config';
import { AuthTokenBuilder } from '../../utils';
import { GoogleAnalytics, GymResolver } from '../../services';

export interface IProps {
  themeType: ThemeType;
  userProfile: UserProfile;
  recommendedSettings: WorkoutSettings;
  currentPersonaliseSetupScreen: PersonaliseSetupScreens | null;
  currentSelectedGymName: any;
  workoutSettingsIsUpdating: any;
  personaliseSetupWarning: PersonaliseSetupWarning | null;
  openPersonaliseSetupScreen(screen: PersonaliseSetupScreens): void;
  getNextBookingProcessStep(userProfile: UserProfile, processType: any): void;
  getRecommendedSetttingsSuccess(workoutSettings: WorkoutSettings): void;
  clearWarning(): void;
  changeOrientationToPortrait(): void;
  setDefaultEquipment(userProfile: UserProfile, equipment: Equipment[]): void;
}

function PersonaliseWorkoutSetupComponent({
  themeType = ThemeType.light,
  userProfile,
  currentSelectedGymName,
  recommendedSettings,
  currentPersonaliseSetupScreen,
  workoutSettingsIsUpdating,
  personaliseSetupWarning,
  openPersonaliseSetupScreen,
  getNextBookingProcessStep,
  getRecommendedSetttingsSuccess,
  clearWarning,
  changeOrientationToPortrait,
  setDefaultEquipment,
}: IProps) {
  const gym = GymResolver.getByName(currentSelectedGymName);
  const [customSettings, setCustomSettings] = useState<WorkoutSettings>(recommendedSettings);
  const [settingsChanged, setSettingsChanged] = useState<boolean>(false);
  const [shouldSaveDefaultEquipment, setShouldSaveDefaultEquipment] = useState<boolean>(false);
  const portrait: Orientation = 'PORTRAIT';

  useEffect(() => {
    changeOrientationToPortrait();
  }, []);

  const goBack = () => {
    currentPersonaliseSetupScreen === PersonaliseSetupScreens.Overview
      ? getNextBookingProcessStep(userProfile, ProcessTypes.WorkoutSettings)
      : openPersonaliseSetupScreen(PersonaliseSetupScreens.Overview);
  };

  const saveAndClose = () => {
    const customSettingsIgnoredFromProgram = new WorkoutSettings({
      ...customSettings,
      ignoreProgramRecommendations: true,
    });
    if (customSettingsIgnoredFromProgram.cardioOrResistance === CardioOrResistance.Balance) {
      customSettingsIgnoredFromProgram.allowBagContact = false;
    }
    if (shouldSaveDefaultEquipment) {
      setDefaultEquipment(userProfile, customSettingsIgnoredFromProgram.equipment);
    }
    setCustomSettings(customSettingsIgnoredFromProgram);
    getRecommendedSetttingsSuccess(customSettingsIgnoredFromProgram);
    getNextBookingProcessStep(userProfile, ProcessTypes.CustomiseWorkout);
    openPersonaliseSetupScreen(PersonaliseSetupScreens.Overview);
  };

  const closeSetupAndResetOverviewScreen = () => {
    getNextBookingProcessStep(userProfile, ProcessTypes.WorkoutSettings);
    openPersonaliseSetupScreen(PersonaliseSetupScreens.Overview);
  };

  return (
    <View style={styles.container}>
      <View style={styles.container}>
        <PersonaliseWorkoutSetup
          themeType={themeType}
          orientation={portrait}
          gym={gym}
          workoutSettings={customSettings}
          userProfile={userProfile}
          onCompleteSettingChange={(settings: WorkoutSettings, saveDefaultEquipment?: boolean) => {
            setCustomSettings(settings);
            setSettingsChanged(true);
            setShouldSaveDefaultEquipment(saveDefaultEquipment ?? false);
          }}
        />
      </View>

      <PersonaliseWorkoutFooter
        themeType={themeType}
        orientation={portrait}
        currentPersonaliseSetupScreen={currentPersonaliseSetupScreen}
        workoutSettingsIsUpdating={workoutSettingsIsUpdating}
        personaliseSetupWarning={personaliseSetupWarning}
        settingsChanged={settingsChanged}
        goBack={goBack}
        saveAndClose={saveAndClose}
        closeSetupAndResetOverviewScreen={closeSetupAndResetOverviewScreen}
        clearWarning={clearWarning}
        openPersonaliseSetupScreen={openPersonaliseSetupScreen}
      />
    </View>
  );
}

const mapStateToProps = (state): Partial<IProps> => ({
  currentSelectedGymName: state.bookings.bookingWizard.currentSelectedGymName,
  recommendedSettings: recommendedSettingsSelector(state),
  currentPersonaliseSetupScreen: personaliseSetupScreenSelector(state),
  workoutSettingsIsUpdating: workoutSettingsIsUpdatingSelector(state),
  personaliseSetupWarning: personaliseSetupWarningSelector(state),
});

const mapDispatchToProps = (dispatch, ownProps: any) => {
  const userServiceClient = new UserServiceClient(
    getApiConfig(),
    fetch,
    console.log,
    new
    AuthTokenBuilder(),
    GoogleAnalytics,
  );

  return {
    getNextBookingProcessStep: (userProfile, processType) => dispatch(
      getNextBookingStep(userProfile, processType, undefined, undefined),
    ),
    getRecommendedSetttingsSuccess: (workoutSettings: WorkoutSettings) => dispatch(
      workoutSettingsUpdated(workoutSettings),
    ),
    openPersonaliseSetupScreen: (screen: PersonaliseSetupScreens) => dispatch(
      openPersonaliseWorkoutSetupScreen(screen),
    ),
    clearWarning: () => dispatch(clearPersonaliseSetupWarning()),
    changeOrientationToPortrait: () => dispatch(changeOrientation('PORTRAIT')),
    setDefaultEquipment: (userProfile: UserProfile, equipment: Equipment[]) => dispatch(
      setDefaultEquipment(userServiceClient, userProfile, equipment),
    ),
  };
};

export const PersonaliseWorkoutSetupStep = connect(mapStateToProps, mapDispatchToProps)(
  translate(['bookings', 'common'], { wait: true })(PersonaliseWorkoutSetupComponent),
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
